
import React, { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { formatCurrency } from '../utils/formatters';

type PlatformComparisonTableProps = {
  metrics: {
    deliveryOrderCount: number;
    totalPumpdRevenue: number;
    totalUberEatsRevenue: number;
    totalCustomerSavings: number;
    totalRestaurantProfit: number;
    avgCustomerSavings: number;
    avgRestaurantProfit: number;
    revenueDifference: number;
    revenuePercentage: number;
  };
  rawData: any[];
  restaurantMarkups: Record<string, number>;
  selectedRestaurants: string[];
};

const PlatformComparisonTable: React.FC<PlatformComparisonTableProps> = ({ 
  metrics, 
  rawData,
  restaurantMarkups,
  selectedRestaurants
}) => {
  // Calculate detailed metrics from raw data
  const detailedMetrics = useMemo(() => {
    let pumpdCartValue = 0;
    let uberCartValue = 0;
    let pumpdDeliveryFees = 0;
    let uberDeliveryFees = 0;
    let pumpdCommission = 0;
    let uberCommission = 0;
    let pumpdStripeFees = 0;
    let pumpdTips = 0;
    let uberTips = 0;
    let pumpdTotalCost = 0;
    let uberTotalCost = 0;
    let avgMarkup = 0;
    let markupCount = 0;
    
    // Calculate totals from raw data
    rawData.forEach(order => {
      if (order.service_type === 'delivery') {
        pumpdCartValue += parseFloat(order.cart_value) || 0;
        uberCartValue += parseFloat(order.ubereats_menu_price) || 0;
        pumpdDeliveryFees += parseFloat(order.delivery_fee_on_pumpd) || 0;
        uberDeliveryFees += parseFloat(order.ubereats_total_delivery_fees) || 0;
        pumpdCommission += parseFloat(order.pumpd_commission) || 0;
        uberCommission += parseFloat(order.ubereats_commission) || 0;
        pumpdStripeFees += parseFloat(order.pumpd_stripe_fee) || 0;
        pumpdTips += parseFloat(order.tip) || 0;
        uberTips += parseFloat(order.tip) || 0;
        
        // Use pre-calculated total cost values that include tips
        pumpdTotalCost += parseFloat(order.pumpd_total_cost_for_customer) || 0;
        uberTotalCost += parseFloat(order.ubereats_cost_to_customer) || 0;
        
        // Track markup for averaging
        const markup = parseFloat(order.ubereats_markup) || 0;
        if (markup > 0) {
          avgMarkup += markup;
          markupCount++;
        }
      }
    });
    
    // Calculate average markup or use from restaurantMarkups
    if (markupCount > 0) {
      avgMarkup = avgMarkup / markupCount;
    } else if (Object.keys(restaurantMarkups).length > 0) {
      const markupValues = Object.values(restaurantMarkups);
      avgMarkup = markupValues.reduce((sum, val) => sum + val, 0) / markupValues.length;
    }
    
    return {
      pumpdCartValue,
      uberCartValue,
      pumpdDeliveryFees,
      uberDeliveryFees,
      pumpdTotalCost,
      uberTotalCost,
      pumpdCommission,
      uberCommission,
      pumpdStripeFees,
      pumpdTips,
      uberTips,
      avgMarkup
    };
  }, [rawData, restaurantMarkups]);
  
  const formatDifference = (pumpd: number, uber: number) => {
    const diff = uber - pumpd;
    return formatCurrency(diff);
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Platform Comparison - Delivery Orders</CardTitle>
        <CardDescription>Detailed comparison of delivery orders placed through Pump'd vs. the same orders if they were placed on UberEats</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-secondary/50">
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Metric</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Pump'd</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">UberEats</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Difference</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium">Delivery Order Count</td>
                  <td className="px-4 py-3 text-sm">{metrics.deliveryOrderCount}</td>
                  <td className="px-4 py-3 text-sm">{metrics.deliveryOrderCount}</td>
                  <td className="px-4 py-3 text-sm">—</td>
                </tr>
                <tr className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium">Cart Value</td>
                  <td className="px-4 py-3 text-sm">{formatCurrency(detailedMetrics.pumpdCartValue)}</td>
                  <td className="px-4 py-3 text-sm">
                    {formatCurrency(detailedMetrics.uberCartValue)}
                    {detailedMetrics.avgMarkup > 0 && (
                      <span className="text-xs text-muted-foreground ml-1">
                        ({detailedMetrics.avgMarkup.toFixed(1)}% markup)
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">{formatCurrency(detailedMetrics.uberCartValue - detailedMetrics.pumpdCartValue)}</td>
                </tr>
                <tr className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium">Total Delivery Fees</td>
                  <td className="px-4 py-3 text-sm">{formatCurrency(detailedMetrics.pumpdDeliveryFees)}</td>
                  <td className="px-4 py-3 text-sm">{formatCurrency(detailedMetrics.uberDeliveryFees)}</td>
                  <td className="px-4 py-3 text-sm">{formatCurrency(detailedMetrics.uberDeliveryFees - detailedMetrics.pumpdDeliveryFees)}</td>
                </tr>
                <tr className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium">Tips</td>
                  <td className="px-4 py-3 text-sm">{formatCurrency(detailedMetrics.pumpdTips)}</td>
                  <td className="px-4 py-3 text-sm">{formatCurrency(detailedMetrics.uberTips)}</td>
                  <td className="px-4 py-3 text-sm">{formatCurrency(detailedMetrics.uberTips - detailedMetrics.pumpdTips)}</td>
                </tr>
                <tr className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium">Total Cost to Customer</td>
                  <td className="px-4 py-3 text-sm">{formatCurrency(detailedMetrics.pumpdTotalCost)}</td>
                  <td className="px-4 py-3 text-sm">{formatCurrency(detailedMetrics.uberTotalCost)}</td>
                  <td className="px-4 py-3 text-sm text-green-600">
                    {formatDifference(detailedMetrics.pumpdTotalCost, detailedMetrics.uberTotalCost)} Saved by customers
                  </td>
                </tr>
                <tr className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium">Commission Fees</td>
                  <td className="px-4 py-3 text-sm">{formatCurrency(detailedMetrics.pumpdCommission)}</td>
                  <td className="px-4 py-3 text-sm">{formatCurrency(detailedMetrics.uberCommission)}</td>
                  <td className="px-4 py-3 text-sm">{formatCurrency(detailedMetrics.uberCommission - detailedMetrics.pumpdCommission)}</td>
                </tr>
                <tr className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium">Stripe Fees</td>
                  <td className="px-4 py-3 text-sm">{formatCurrency(detailedMetrics.pumpdStripeFees)}</td>
                  <td className="px-4 py-3 text-sm">—</td>
                  <td className="px-4 py-3 text-sm">—</td>
                </tr>
                <tr className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium">Net Revenue</td>
                  <td className="px-4 py-3 text-sm">{formatCurrency(metrics.totalPumpdRevenue)}</td>
                  <td className="px-4 py-3 text-sm">{formatCurrency(metrics.totalUberEatsRevenue)}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={metrics.revenueDifference >= 0 ? "text-green-600" : "text-red-600"}>
                      {formatCurrency(metrics.revenueDifference)} Profit added
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PlatformComparisonTable;
